import Cookies from 'js-cookie';
import ArrowIcon from '@/components/icons/sidebar/Arrow';
import NavigationDropdown from './components/sidebar/NavigationDropdown';
import BrandedLogo from '@/components/BrandedLogo';
import { MenuIcon, XIcon, LogOut } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/core/global-store';
import {
    closeSidebarOnMobile,
    toggleSidebar,
    toggleSidebarOnMobile,
} from '@/core/global-store/features/ui/uiSlice';
import { clearSessionAndGoToLogin } from '@/lib/axios/axios-global';
import { cn } from '@/lib/utils';
import { navigation as sidebarNavigation, navigationBottom } from '@/core/global-data/SidebarData';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissionNames } from '@/features/accessControl/hooks/usePermission';
import { useIsOwner } from '@/hooks/useIsOwner';
import { useIsTenantSuperAdmin } from '@/hooks/useIsTenantSuperAdmin';
import { useSubscription } from '@/hooks/useSubscription';

const Sidebar = () => {
    const lng = Cookies.get('i18next');
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
    const isSidebarOpenOnMobile = useAppSelector((state) => state.ui.isSidebarOpenOnMobile);
    const [activeTab, setActiveTab] = useState<string>(t('dashboard'));
    const [secondaryActiveTab, setSecondaryActiveTab] = useState<string>('');
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
    );
    const navigate = useNavigate();
    const permissionNames = usePermissionNames();
    const isOwner = useIsOwner();
    const isTenantSuperAdmin = useIsTenantSuperAdmin();
    const subscription = useSubscription();

    const planFeatures = subscription?.plan?.features ?? null;

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 768px)');
        const onChange = () => setIsDesktop(mediaQuery.matches);
        mediaQuery.addEventListener('change', onChange);
        return () => mediaQuery.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (isDesktop && isSidebarOpenOnMobile) {
            dispatch(closeSidebarOnMobile());
        }
    }, [isDesktop, isSidebarOpenOnMobile, dispatch]);

    const navigation = useMemo(() => {
        const hasAny = (perms?: string[]) => !perms?.length || perms.some((p) => permissionNames.includes(p));
        const hasPlanFeature = (feature?: string) => {
            if (!feature) return true;
            if (isOwner || isTenantSuperAdmin) return true;
            if (!planFeatures) return true;
            return Boolean(planFeatures[feature]);
        };

        return sidebarNavigation
            .filter((item) => (isOwner ? item.ownerOnly === true : !item.ownerOnly))
            .map((item) => ({
                ...item,
                navigation: item.navigation?.filter((c) => hasAny(c.permissionsAny) && hasPlanFeature(c.planFeature)),
            }))
            .filter((item) => {
                if (item.ownerOnly) {
                    return isOwner && (item.navigation?.length ?? 0) > 0;
                }
                if (item.navigation) {
                    return item.navigation.length > 0;
                }
                return hasAny(item.permissionsAny) && hasPlanFeature(item.planFeature);
            });
    }, [permissionNames, isOwner, isTenantSuperAdmin, planFeatures]);

    useEffect(() => {
        // Handle sidebar active tabs from URL updates
        const currentPath = location.pathname;
        let mainNavItem;

        if (currentPath.length > 1) {
            mainNavItem = navigation.slice(1).find((item) => {
                return currentPath.replace(/\//g, '').includes(item.to.replace(/\//g, ''));
            });
        } else {
            mainNavItem = navigation[0];
        }

        const bottomNavItem = navigationBottom.find((item) => item.to === currentPath);

        if (mainNavItem) {
            if (mainNavItem.name === 'self_services') {
                setActiveTab('');
            } else {
                setActiveTab(t(mainNavItem.name));
            }
            if (mainNavItem.navigation) {
                const secondNavItem = mainNavItem.navigation.find((item) => item.to === currentPath);
                setSecondaryActiveTab(secondNavItem ? t(secondNavItem.name) : '');
            }
        } else if (bottomNavItem) {
            setActiveTab(t(bottomNavItem.name));
        }
    }, [location.pathname, navigation, t]);

    const toggle = () => dispatch(toggleSidebar());
    const toggleMobile = () => dispatch(toggleSidebarOnMobile());
    const closeMobile = () => dispatch(closeSidebarOnMobile());
    /** Desktop collapse is independent of the mobile drawer state. */
    const isNavExpanded = isDesktop ? isSidebarOpen : isSidebarOpenOnMobile;

    const handleNavLinkClick = () => {
        if (!isDesktop) {
            closeMobile();
        }
    };

    const handleLogout = () => {
        clearSessionAndGoToLogin();
        if (!isDesktop) {
            closeMobile();
        }
    };
    return (
        <>
            {isSidebarOpenOnMobile ? (
                <button
                    type="button"
                    aria-label="إغلاق القائمة"
                    className="fixed inset-0 z-30 bg-black/40 md:hidden"
                    onClick={toggleMobile}
                />
            ) : null}

            {/* Mobile menu button */}
            <button
                className="md:hidden fixed top-4 end-4 z-50 p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                onClick={() => toggleMobile()}
            >
                {isSidebarOpenOnMobile ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed top-0 start-0 z-40 bg-primery-blue-500 shadow-lg transition-all duration-300 ease-in-out',
                    {
                        'max-md:w-full max-md:h-full max-md:translate-x-0': isSidebarOpenOnMobile,
                        'w-0 h-0 overflow-hidden md:overflow-visible md:w-[240px] md:h-screen md:sticky md:top-0':
                            !isSidebarOpenOnMobile && isSidebarOpen,
                        'w-0 h-0 overflow-hidden md:overflow-visible md:w-20 md:h-screen md:sticky md:top-0':
                            !isSidebarOpenOnMobile && !isSidebarOpen,
                        'translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng === 'ar',
                        '-translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng !== 'ar',
                    },
                )}
            >
                <div className="flex flex-col h-full text-white-blue-50">
                    {/* Logo and Toggle — single control stays in header (not inside scrollable nav) */}
                    <div
                        className={cn('relative z-20 my-4 flex min-h-8 shrink-0', {
                            'flex-col items-center justify-center gap-2 px-2': !isNavExpanded,
                            'ms-6 me-2 items-center justify-between': isNavExpanded,
                        })}
                    >
                        <span className="cursor-pointer shrink-0" onClick={() => navigate('/')}>
                            {isNavExpanded ? <BrandedLogo /> : <BrandedLogo width="50" height="32" />}
                        </span>
                        <button
                            type="button"
                            aria-label={isSidebarOpen ? 'طي القائمة الجانبية' : 'توسيع القائمة الجانبية'}
                            aria-expanded={isSidebarOpen}
                            onClick={toggle}
                            className={cn(
                                'hidden shrink-0 items-center justify-center rounded-lg p-2 transition-transform hover:bg-white-blue-50/30 md:flex',
                                { 'rotate-180': !isSidebarOpen },
                            )}
                        >
                            <ArrowIcon />
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <nav className="flex-1 sm:ms-6 ms-4 font-medium text-base leading-5 mt-2 overflow-y-auto custom-scrollbar">
                        {navigation.map((item, index) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <div key={index}>
                                    <NavigationDropdown
                                        to={item.to}
                                        Icon={Icon}
                                        activeTab={activeTab}
                                        name={item.name}
                                        isSidebarOpen={isNavExpanded}
                                        setActiveTab={setActiveTab}
                                        secondaryActiveTab={secondaryActiveTab}
                                        setSecondaryActiveTab={setSecondaryActiveTab}
                                        navigation={item.navigation}
                                        dropdownOnly={item.dropdownOnly}
                                        onLinkClick={handleNavLinkClick}
                                    />
                                </div>
                            );
                        })}
                    </nav>

                    <nav
                        className={cn('mx-5 mt-auto mb-8 font-medium text-base leading-5', {
                            'me-10 ms-0': !isNavExpanded,
                        })}
                    >
                        {navigationBottom.map((item) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <button
                                    key={t(item.name)}
                                    onClick={() => {
                                        setActiveTab(t(item.name));
                                        if (item.to) {
                                            navigate(item.to);
                                            handleNavLinkClick();
                                        }
                                    }}
                                    className={cn(
                                        'w-full flex items-center justify-start hover:text-primery-blue-700 rounded-lg mb-1 p-2',
                                        {
                                            'bg-white-blue-50 text-primery-blue-500': activeTab === t(item.name),
                                            'hover:bg-white-blue-300': activeTab !== t(item.name),
                                            'ms-1': !isNavExpanded,
                                        },
                                    )}
                                >
                                    <Icon
                                        className={`w-6 h-6 flex items-center justify-center ${isNavExpanded ? 'me-2' : 'mx-auto'}`}
                                    />
                                    <span className={`${!isNavExpanded && 'hidden'} text-start`}>{t(item.name)}</span>
                                </button>
                            );
                        })}
                        <button
                            onClick={handleLogout}
                            className={cn(
                                'w-full flex items-center justify-start hover:text-red-200 rounded-lg mt-2 pt-2 border-t border-white-blue-50/30 p-2 text-white-blue-50',
                                { 'ms-1': !isNavExpanded },
                            )}
                        >
                            <LogOut className={`w-6 h-6 flex-shrink-0 ${isNavExpanded ? 'me-2' : 'mx-auto'}`} />
                            <span className={`${!isNavExpanded && 'hidden'} text-start`}>{t('logout')}</span>
                        </button>
                    </nav>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
